import React, {useEffect, useMemo, useState} from 'react'
import {FmkNalogiStavkeModel} from '../../../models/FmkNaloziModel'
import {flexRender, getCoreRowModel, useReactTable} from '@tanstack/react-table'
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent'
import IndeterminateCheckbox from '../../../components/IndeterminateCheckbox/IndeterminateCheckbox'

interface Props {
    data: FmkNalogiStavkeModel
    setStavkaData: Function
    singleSelection?: boolean
}

const FmkNalog: React.FC<Props> = ({data, setStavkaData, singleSelection = false}) => {
    const tableData = useMemo(() => data.stavka, [data])
    const nalogData = useMemo(() => data.nalog, [data])

    const [loading] = useState(false)
    const [rowSelection, setRowSelection] = useState({})

    const columns = [
        {
            id: 'id',
            header: ({table}) => (
                <div className='d-flex justify-content-center'>
                    {!singleSelection && (
                        <IndeterminateCheckbox
                            {...{
                                checked: table.getIsAllRowsSelected(),
                                indeterminate: table.getIsSomeRowsSelected(),
                                onChange: table.getToggleAllRowsSelectedHandler(),
                            }}
                        />
                    )}
                </div>
            ),
            cell: ({row}: any) => (
                <div className='d-flex justify-content-center'>
                    <IndeterminateCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                    />
                </div>
            ),
        },
        {
            id: '#',
            header: '#',
            accessorFn: (row: any, index: number) => index + 1,
            cell: ({getValue}: any) => <span className='text-muted'>{getValue()}</span>,
        },
        {
            id: 'fmkId',
            header: 'FMK ID',
            accessorFn: (row: any) => row.fmkId ?? row.FmkId ?? '-',
            cell: ({getValue}: any) => (
                <span className='fw-semibold text-gray-800 text-nowrap'>{getValue()}</span>
            ),
        },
        {
            id: 'Šifra',
            header: 'Šifra',
            accessorKey: 'IdArtikla',
            cell: ({getValue}: any) => (
                <span className='fw-semibold text-gray-800 text-nowrap'>{getValue()}</span>
            ),
        },
        {
            id: 'Naziv',
            header: 'Naziv',
            accessorKey: 'Naziv',
            cell: ({getValue}: any) => <span className='text-gray-800'>{getValue()}</span>,
        },
        {
            id: 'Norma',
            header: 'Norma',
            accessorKey: 'NormaMin',
            cell: ({row}: {row: any}) =>
                row.original.NormaMin > 0 ? (
                    <span className='badge badge-light-success fw-semibold'>DA</span>
                ) : (
                    <span className='badge badge-light-danger fw-semibold'>NE</span>
                ),
        },
        {
            id: 'JM',
            header: 'JM',
            accessorKey: 'IdJedMjere',
            cell: ({getValue}: any) => <span className='text-nowrap'>{getValue()}</span>,
        },
        {
            id: 'Količina',
            header: 'Količina',
            accessorKey: 'Kolicina',
            cell: ({getValue}: any) => (
                <span className='fw-semibold text-gray-800 text-nowrap'>{getValue()}</span>
            ),
        },
        {
            id: 'Status',
            header: 'Status',
            accessorKey: 'statusi.Naziv',
            cell: ({row}: any) => {
                const bg = row.original?.statusi?.Boja || '#6c757d'
                const label = row.original?.statusi?.Naziv || '-'

                return (
                    <span
                        className='badge fw-semibold text-nowrap'
                        style={{
                            backgroundColor: bg,
                            color: '#fff',
                            padding: '6px 10px',
                            borderRadius: '999px',
                            letterSpacing: '0.2px',
                        }}
                        title={label}
                    >
            {label}
          </span>
                )
            },
        },
    ]

    const table = useReactTable({
        data: tableData,
        columns,
        state: {rowSelection},
        getCoreRowModel: getCoreRowModel(),
        onRowSelectionChange: setRowSelection,
        enableMultiRowSelection: !singleSelection,
        enableRowSelection: (row) => {
            const allowedIds = [38, 22, 23]
            return allowedIds.includes(row.original.statusi.Id)
        },
    })

    useEffect(() => {
        const stavka = table.getSelectedRowModel().rows.map((item: any) => {
            return {
                Id: item.original.Id.toString(),
                fmkId: item.original.fmkId ?? item.original.FmkId,
                SifraArtikla: item.original.IdArtikla,
                NazivArtikla: item.original.Naziv,
                NormaMin: item.original.NormaMin,
                Kolicina: item.original.Kolicina,
                Jm: item.original.IdJedMjere,
                statusi: item.original.statusi,
            }
        })
        setStavkaData(stavka)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowSelection])

    return (
        <div className='w-100'>
            {/* Header card */}
            <div className='card shadow-sm mb-6'>
                <div className='card-body p-4 p-md-6'>
                    <div className='d-flex flex-column flex-md-row justify-content-between gap-4'>
                        <div className='d-flex flex-column gap-1'>
                            <div className='text-muted'>Kupac</div>
                            <div className='fs-4 fw-bolder text-gray-900'>{nalogData?.Kupac ?? '-'}</div>
                        </div>

                        <div className='d-flex flex-column gap-1'>
                            <div className='text-muted'>Nalog kreirao</div>
                            <div className='fw-semibold text-gray-800'>{nalogData?.Korisnik ?? '-'}</div>
                        </div>

                        <div className='d-flex align-items-start align-items-md-center justify-content-md-end'>
              <span className='badge badge-light-primary py-2 px-3'>
                Odabrano: {table.getSelectedRowModel().rows.length}
              </span>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className='card'>
                    <div className='card-body p-6'>
                        <LoadingComponent />
                    </div>
                </div>
            ) : (
                <div className='card shadow-sm'>
                    <div className='card-body p-0'>
                        {/* Mobile-friendly horizontal scroll */}
                        <div className='table-responsive'>
                            <table className='table align-middle table-row-dashed mb-0'>
                                <thead
                                    className='text-uppercase text-gray-600 fw-bold fs-7'
                                    style={{position: 'sticky', top: 0, zIndex: 1}}
                                >
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id} className='bg-light'>
                                        {headerGroup.headers.map((header) => (
                                            <th
                                                key={header.id}
                                                className='px-4 py-4 text-nowrap'
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{cursor: 'pointer', userSelect: 'none'}}
                                            >
                                                <div className='d-flex align-items-center gap-2'>
                                                    <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>

                                                    {(() => {
                                                        const sort = header.column.getIsSorted()
                                                        const sortIcon = sort === 'asc' ? '🔼' : sort === 'desc' ? '🔽' : null
                                                        return sortIcon ? <span className='text-muted'>{sortIcon}</span> : null
                                                    })()}
                                                </div>
                                            </th>

                                        ))}
                                    </tr>
                                ))}
                                </thead>

                                <tbody className='fw-semibold text-gray-700'>
                                {table.getRowModel().rows && table.getRowModel().rows.length > 0 ? (
                                    table.getRowModel().rows.map((row) => (
                                        <tr
                                            key={row.id}
                                            className={row.getIsSelected() ? 'bg-light-success' : ''}
                                            style={{transition: 'background-color 120ms ease'}}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id} className='px-4 py-4'>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length}>
                                            <div className='d-flex text-center w-100 align-content-center justify-content-center py-10 text-muted'>
                                                No matching records found
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                        {/* subtle footer help for mobile */}
                        <div className='px-4 py-3 border-top text-muted small'>
                            Tip: na mobitelu prevuci tabelu lijevo/desno.
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default FmkNalog
